import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, cx } from '@atlaskit/css';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import type { DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/types';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const playerWidth = 380;
// TEMP: Need to figure out plans for miniplayer and dynamic width calculations
//const miniplayerWidth = 274;
const initialTopPosition = 104;
const initialRightOffset = playerWidth + 32; // width + space.400

const styles = cssMap({
	floatingStyles: {
		position: 'fixed',
		// @ts-ignore: There are no space tokens larger than 80px (space.1000)
		top: `${initialTopPosition}px`, // under the top nav
		right: token('space.400'), // Tied to initialRightOffset
		zIndex: 510, // modal
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.xlarge'),
		textAlign: 'center',
	},
	idleStyles: {
		//////////////
		// TEMP: Need to figure out plans for miniplayer and dynamic width calculations
		width: `${playerWidth}px`, // width of full player
		//width: 'auto',
		//minWidth: `${miniplayerWidth}px`, // width of minified player
		//////////////
		cursor: 'grab',
	},
	draggingStyles: {
		width: `${playerWidth}px`, // width of full player
		cursor: 'grabbing',
	},
});

type Position = {
	top: number;
	left: number;
};

// Prevents the miniplayer from being dragged past the boundaries of a given dimension of the window
const getBoundedDimension = (dimension: number, maxDimension: number): number => {
	if (dimension < 0 || dimension > maxDimension) {
		return dimension < 0 ? 0 : maxDimension;
	}

	return dimension;
};

// Prevent clicking from blurring/closing other UI elements like modals/dialogs
const stopClickPropagation: React.MouseEventHandler<HTMLElement> = (
	event: React.MouseEvent,
): void => event.stopPropagation();

export type DraggableAudioProps = {
	children: React.ReactNode;
};

export const DraggableAudio = ({ children }: DraggableAudioProps): React.JSX.Element | null => {
	const document = getDocument();
	if (!document) {
		return null;
	}

	return <DraggableAudioInternal document={document}>{children}</DraggableAudioInternal>;
};

export type DraggableAudioInternalProps = {
	children: React.ReactNode;
	document: Document;
};

const DraggableAudioInternal = ({
	children,
	document,
}: DraggableAudioInternalProps): React.JSX.Element => {
	const draggableRef = useRef<HTMLElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [currentPosition, setCurrentPosition] = useState<Position>(() => ({
		top: initialTopPosition,
		left: document.body.clientWidth - initialRightOffset,
	}));

	const setPosition = useCallback(
		(element: HTMLElement, location: DragLocationHistory, isDrop = false) => {
			// Difference between the current cursor position with that of the beginning of the drag
			const clientYDelta = location.current.input.clientY - location.initial.input.clientY;
			const clientXDelta = location.current.input.clientX - location.initial.input.clientX;

			// Proposed new top and left of the draggable position
			const newTop = currentPosition.top + clientYDelta;
			const newLeft = currentPosition.left + clientXDelta;

			// Prevent the boundaries of the draggable from being dragged beyond the window boundaries
			const boundedNewTop = getBoundedDimension(newTop, window.innerHeight - element.offsetHeight);
			const boundedNewLeft = getBoundedDimension(
				newLeft,
				document.body.clientWidth - element.offsetWidth,
			);

			if (isDrop) {
				// Reset transform and physically move element in the DOM
				element.style.setProperty('transform', 'initial');
				element.style.setProperty('top', `${boundedNewTop}px`);
				element.style.setProperty('left', `${boundedNewLeft}px`);

				setCurrentPosition({
					top: boundedNewTop,
					left: boundedNewLeft,
				});
			} else {
				const boundedDeltaY = boundedNewTop - currentPosition.top;
				const boundedDeltaX = boundedNewLeft - currentPosition.left;

				// Apply CSS transform to dragged spot without physically moving the element in the DOM
				element.style.setProperty('transform', `translate(${boundedDeltaX}px, ${boundedDeltaY}px)`);
			}
		},
		[currentPosition, document],
	);

	// Setup drag-and-drop
	useEffect(() => {
		const element = draggableRef.current;
		if (!element) {
			return;
		}

		return combine(
			draggable({
				element,
				onGenerateDragPreview({ nativeSetDragImage }) {
					// We'll drag the actual element so hide the native drag preview
					disableNativeDragPreview({ nativeSetDragImage });
				},
				onDragStart: () => {
					setIsDragging(true);
				},
				onDrag: ({ location }) => {
					setPosition(element, location);
				},
				onDrop: ({ location }) => {
					setPosition(element, location, true);
					setIsDragging(false);
				},
			}),
			dropTargetForElements({
				element: document.body,
			}),
		);
	});

	return (
		<Box
			ref={draggableRef}
			xcss={cx(styles.floatingStyles, isDragging ? styles.draggingStyles : styles.idleStyles)}
			onClick={stopClickPropagation}
			testId="audio-draggable"
			role="none"
		>
			{children}
		</Box>
	);
};
